/**
 * AutoTrailSwitch
 * ─────────────────────────────────────────────────────────────────────────────
 * Per-trade inline widget showing Auto-Trail state with a snooze dialog.
 *
 * States:
 *   waiting  — trade not yet eligible (timer shows when it will activate)
 *   on       — trailing is active
 *   snoozed  — user muted it; shows remaining snooze time
 *
 * Spike mode is shown as an additional badge when active.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Chip, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, ToggleButtonGroup, ToggleButton, TextField
} from '@mui/material';
import TrailIcon from '@mui/icons-material/Timeline';
import SpikeIcon from '@mui/icons-material/Bolt';
import { useAppTheme } from '../../hooks/useAppTheme';
import trailService, { TrailStatus } from '../../services/trail.service';

interface Props {
  tradeId: number;
  /** Pre-loaded status (from trade row data — avoids extra API call on mount). */
  initialStatus?: Partial<TrailStatus>;
  compact?: boolean;
}

const SNOOZE_OPTIONS = [5, 10, 15, 30];

export const AutoTrailSwitch: React.FC<Props> = ({ tradeId, initialStatus, compact }) => {
  const { C } = useAppTheme();
  const [status, setStatus]     = useState<TrailStatus | null>(null);
  const [loading, setLoading]   = useState(false);
  const [dialogOpen, setDialog] = useState(false);
  const [snoozeMin, setSnoozeMin] = useState<number | ''>('');
  const [customMin, setCustomMin] = useState('');

  useEffect(() => {
    if (initialStatus?.uiState && !loading) {
      setStatus(initialStatus as TrailStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId, initialStatus?.uiState, initialStatus?.autoTrailEnabled,
      initialStatus?.currentTrailingSl, initialStatus?.trailingMode]);

  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceUpdate(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await trailService.getStatus(tradeId);
      setStatus(s);
    } catch { /* ignore */ }
    setLoading(false);
  }, [tradeId]);

  const handleClick = () => {
    if (!status) { load(); return; }
    if (status.uiState === 'snoozed' || status.uiState === 'disabled') {
      // Both snooze and permanent-off resume the same way.
      handleResume();
    } else {
      // on / waiting → open the manage dialog (snooze for N min OR turn off).
      setSnoozeMin('');
      setCustomMin('');
      setDialog(true);
    }
  };

  const handleSnooze = async () => {
    const mins = Number(snoozeMin || customMin);
    if (!mins || mins < 1) return;
    setLoading(true);
    try {
      const s = await trailService.snooze(tradeId, mins);
      setStatus(s);
    } catch { /* ignore */ }
    setLoading(false);
    setDialog(false);
  };

  const handleResume = async () => {
    setLoading(true);
    try {
      const s = await trailService.resume(tradeId);
      setStatus(s);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      const s = await trailService.disable(tradeId);
      setStatus(s);
    } catch { /* ignore */ }
    setLoading(false);
    setDialog(false);
  };

  // ── Derived display values ────────────────────────────────────────────────
  const uiState = status?.uiState ?? 'waiting';
  const trailingMode = status?.trailingMode ?? 'off';
  const currentSl = status?.currentTrailingSl;
  const spikeModeActive = status?.spikeModeActive ?? false;
  const isBreaching = status?.slBreachActive === true && uiState === 'on';

  let chipLabel = '';
  let chipColor: string = C.dim;
  let chipBg: string = C.dimMuted;
  let tooltip = '';

  if (isBreaching) {
    chipLabel = compact ? '⚠ Breach' : '⚠ Breaching';
    chipColor = C.bear;
    chipBg    = C.bearMuted;
    let breachDetails = '';
    if (status?.slBreachStartedAt) {
      const secs = Math.round((Date.now() - new Date(status.slBreachStartedAt).getTime()) / 1000);
      breachDetails = ` · breached ${secs}s ago`;
    }
    tooltip = `BREACHING — SL touched${breachDetails}${currentSl ? ` · TSL: ${currentSl.toFixed(2)}` : ''} · hold window active before auto square-off`;
  } else if (uiState === 'waiting') {
    // Activation is gated SOLELY by mfeRMultiple ≥ trailSlActivationR. Time-elapsed
    // is no longer a gate.
    const rThreshold = status?.trailSlActivationR ?? 2;
    const currentR   = status?.mfeRMultiple ?? 0;
    const pendingFragment = rThreshold > 0 ? `${currentR.toFixed(1)}/${rThreshold}R` : '';
    chipLabel = compact
      ? (pendingFragment ? `OFF · ${pendingFragment}` : 'OFF')
      : (pendingFragment ? `Trail OFF · ${pendingFragment}` : 'Trail OFF');
    chipColor = C.dim;
    chipBg    = C.dimMuted;
    tooltip = rThreshold <= 0
      ? `WAITING — R-multiple threshold is 0; trailing should be on. (Will activate on next evaluate cycle.)`
      : `CURRENT: Initial SL is active · breach uses ISL value with normal/spike hold-window · NEXT: Trailing SL takes over once MFE reaches ${rThreshold}R (now ${currentR.toFixed(2)}R)`;
  } else if (uiState === 'snoozed') {
    let minsLeft = '';
    let expiryStr = '';
    if (status?.autoTrailMutedUntil) {
      const diff = Math.ceil((new Date(status.autoTrailMutedUntil).getTime() - Date.now()) / 60_000);
      minsLeft = diff > 0 ? ` ${diff}m` : '';
      expiryStr = new Date(status.autoTrailMutedUntil).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    chipLabel = compact ? `Snoozed${minsLeft}` : `Snoozed${minsLeft}`;
    chipColor = C.warn;
    chipBg    = C.warnMuted;
    tooltip   = `SNOOZED — trail paused${expiryStr ? ` until ${expiryStr}` : ''}${minsLeft ? ` (${minsLeft.trim()} remaining)` : ''} · click to resume`;
  } else if (uiState === 'disabled') {
    chipLabel = compact ? 'Trail OFF' : 'Trail OFF (manual)';
    chipColor = C.bear;
    chipBg    = C.bearMuted;
    tooltip   = 'AUTO-TRAIL OFF — you control the stop-loss manually (drag it on the chart, or edit the SL on this row). No ratchet constraint — you can move it to any level. Breach protection still runs against your SL. Click to re-enable auto-trail.';
  } else {
    // ON — R-multiple gate has passed
    chipLabel = compact ? 'Trail ON' : (trailingMode === 'spike' ? 'Spike Trail' : 'Trail ON');
    chipColor = trailingMode === 'spike' ? C.accent : C.bull;
    chipBg    = trailingMode === 'spike' ? C.accentMuted : C.bullMuted;
    const rThreshold = status?.trailSlActivationR ?? 2;
    const currentR   = status?.mfeRMultiple ?? 0;
    if (trailingMode === 'spike') {
      tooltip = `SPIKE TRAIL — profit-lock mode active · TSL: ${currentSl ? currentSl.toFixed(2) : '—'} · protects unrealized gain`;
    } else {
      const activatedStr = status?.autoTrailActivatedAt
        ? ` · active since ${new Date(status.autoTrailActivatedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
        : '';
      const rStatus = rThreshold > 0 ? ` · ${currentR.toFixed(2)}R / ${rThreshold}R threshold met` : '';
      tooltip = `TRAIL ON — TSL: ${currentSl ? currentSl.toFixed(2) : '—'}${rStatus}${activatedStr} · click to snooze`;
    }
  }

  const chipBorderColor = trailingMode === 'spike' ? C.accentBorder : chipColor === C.bear ? C.bearBorder : chipColor === C.warn ? C.warnBorder : chipColor === C.bull ? C.bullBorder : C.dimMuted;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={tooltip} arrow>
          <Chip
            size="small"
            icon={loading
              ? <CircularProgress size={10} sx={{ color: chipColor }} />
              : (spikeModeActive ? <SpikeIcon sx={{ fontSize: 12 }} /> : <TrailIcon sx={{ fontSize: 12 }} />)
            }
            label={chipLabel}
            onClick={handleClick}
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              cursor: 'pointer',
              // Transparent, like the other row badges: this sits inside a tinted trade row
              // and a second translucent fill took the label to 4.45:1. The border carries
              // the state colour.
              bgcolor: 'transparent',
              color: chipColor,
              border: `1px solid ${chipBorderColor}`,
              '& .MuiChip-icon': { color: chipColor, ml: 0.3 },
            }}
          />
        </Tooltip>
      </Box>

      {/* ── Snooze dialog ─────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Manage Auto-Trail</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            <b>Snooze</b> pauses trailing for a set time, then it auto-resumes (the SL stays where it is meanwhile).
            <br /><b>Turn Off</b> disables auto-trail entirely until you re-enable it, and hands SL control back to you —
            drag it on the chart or edit it on the trade row to any level (no ratchet constraint). Breach protection still
            runs against your SL.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
            Snooze for:
          </Typography>
          <ToggleButtonGroup
            value={snoozeMin}
            exclusive
            onChange={(_, v) => { setSnoozeMin(v); setCustomMin(''); }}
            size="small"
            sx={{ flexWrap: 'wrap', gap: 0.5, mb: 2 }}
          >
            {SNOOZE_OPTIONS.map(m => (
              <ToggleButton key={m} value={m} sx={{ px: 2, fontWeight: 700 }}>
                {m} min
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="Custom minutes"
            type="number"
            value={customMin}
            onChange={(e) => { setCustomMin(e.target.value); setSnoozeMin(''); }}
            inputProps={{ min: 1, max: 1440 }}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button color="error" variant="outlined" onClick={handleDisable}>
            Turn Off
          </Button>
          <Box>
            <Button onClick={() => setDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!snoozeMin && !customMin}
              onClick={handleSnooze}
              sx={{ ml: 1 }}
            >
              Snooze
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AutoTrailSwitch;
